import { defineMessages } from 'react-intl';

export const messages = {
  details: defineMessages({
    duration: {
      id: 'manage-services.services.details.duration',
      defaultMessage: '{duration, number} min',
    },
    placeholder: {
      id: 'manage-services.services.details.placeholder',
      defaultMessage: 'Select a service to see more details',
    },
    edit: {
      id: 'manage-services.services.details.edit',
      defaultMessage: 'Edit',
    },
    submitForReview: {
      id: 'manage-services.services.details.submit-for-review',
      defaultMessage: 'Submit for review',
    },
    deleteService: {
      id: 'manage-services.services.details.delete',
      defaultMessage: 'Delete Service',
    },
    missingObjectsTitle: {
      id: 'manage-services.services.details.missing-objects-title',
      defaultMessage: 'Missing Practitioner or Equipment',
    },
    missingObjectsCopy: {
      id: 'manage-services.services.details.missing-objects-copy',
      defaultMessage: 'Associate at least one practitioner or equipment to make this service bookable.',
    },
    availablePractitioners: {
      id: 'manage-services.services.details.available-practitioners',
      defaultMessage: 'Available practitioners',
    },
    availableEquipment: {
      id: 'manage-services.services.details.available-equipment',
      defaultMessage: 'Available equipment',
    },
    missingPractitionersLink: {
      id: 'manage-services.services.details.missing-practitioners-link',
      defaultMessage: 'You have no associated practitioners. <a>Add practitioner</a>',
    },
    missingEquipmentLink: {
      id: 'manage-services.services.details.missing-equipment-link',
      defaultMessage: 'You have no associated equipment. <a>Add equipment</a>',
    },
    priceTooltip: {
      id: 'manage-services.services.details.price-tooltip',
      defaultMessage:
        'Your payout is calculated based on your provided price for this service. For service tax clarifications, please see <link>here</link>.',
    },
    pendingReview: {
      id: 'manage-services.services.details.pending-review',
      defaultMessage: 'Pending review',
    },
  }),
  deleteModal: defineMessages({
    title: {
      id: 'manage-services.services.delete-modal.title',
      defaultMessage: 'Delete service',
    },
    description: {
      id: 'manage-services.services.delete-modal.description',
      defaultMessage:
        'Are you sure you want to delete this service? Existing reservations will not be canceled. Please cancel reservations directly in the “Appointments” tab.',
    },
    cancel: {
      id: 'manage-services.services.delete-modal.cancel',
      defaultMessage: 'Cancel',
    },
    confirm: {
      id: 'manage-services.services.delete-modal.confirm',
      defaultMessage: 'Confirm',
    },
  }),
  form: defineMessages({
    addService: {
      id: 'manage-services.services.form.add-service',
      defaultMessage: 'New service',
    },
    editService: {
      id: 'manage-services.services.form.edit-service',
      defaultMessage: 'Edit {serviceName}',
    },
    name: {
      id: 'manage-services.services.form.name',
      defaultMessage: 'Name',
    },
    description: {
      id: 'manage-services.services.form.description',
      defaultMessage: 'Description',
    },
    durationLabel: {
      id: 'manage-services.services.form.duration-label',
      defaultMessage: 'Duration',
    },
    namePlaceholder: {
      id: 'manage-services.services.form.name-placeholder',
      defaultMessage: 'Swedish Massage',
    },
    descriptionPlaceholder: {
      id: 'manage-services.services.form.description-placeholder',
      defaultMessage:
        'Share more about this service. This information will become part of the appointment page, and can be seen by ClassPass users.',
    },
    delete: {
      id: 'manage-services.services.form.delete',
      defaultMessage: 'Delete service',
    },
    cancel: {
      id: 'manage-services.services.form.cancel',
      defaultMessage: 'Cancel',
    },
    save: {
      id: 'manage-services.services.form.save',
      defaultMessage: 'Save',
    },
    optional: {
      id: 'manage-services.services.form.optional',
      defaultMessage: 'Optional',
    },
    serviceInfo: {
      id: 'manage-services.services.form.service-info',
      defaultMessage: 'Service info',
    },
    selectDuration: {
      id: 'manage-services.components.duration-picker.select-duration',
      defaultMessage: 'Select a duration',
    },
    duration: {
      id: 'manage-services.components.duration-picker.duration',
      defaultMessage: '{duration, number} min',
    },
    practitionersTitle: {
      id: 'manage-services.services.form.practitioners-title',
      defaultMessage: 'Practitioners',
    },
    practitionersSubtitle: {
      id: 'manage-services.services.form.practitioners-subtitle',
      defaultMessage: 'Select a practitioner to assign them to the service',
    },
    equipmentTitle: {
      id: 'manage-services.services.form.equipment-title',
      defaultMessage: 'Equipment',
    },
    equipmentSubtitle: {
      id: 'manage-services.services.form.equipment-subtitle',
      defaultMessage: 'Select an equipment to assign it to the service',
    },
    bookableName: {
      id: 'manage-services.services.form.bookable-name',
      defaultMessage: 'Name',
    },
    bookableAvailability: {
      id: 'manage-services.services.form.bookable-availability',
      defaultMessage: 'Recurring availability',
    },
    addPractitionersLink: {
      id: 'manage-services.services.form.add-practitioners-link',
      defaultMessage: 'No practitioner in the list? <a>Add new practitioner to ClassPass</a>',
    },
    addEquipmentLink: {
      id: 'manage-services.services.form.add-equipment-list',
      defaultMessage: 'No equipment in the list? <a>Add new equipment to ClassPass</a>',
    },
    pricing: {
      id: 'manage-services.services.form.pricing',
      defaultMessage: 'Pricing',
    },
    payout: {
      id: 'manage-services.services.form.payout',
      defaultMessage: 'Payout',
    },
    pricingInfoBanner: {
      id: 'manage-services.services.form.pricing-info-banner',
      defaultMessage: 'We must verify the price of this service before it becomes available to book.',
    },
    pricingUpdateInfoBanner: {
      id: 'manage-services.services.form.pricing-update-info-banner',
      defaultMessage:
        'If your menu price changes, our team will need to verify the new price before it becomes available to order.',
    },
    notListedOnline: {
      id: 'manage-services.services.form.not-listed-online',
      defaultMessage: 'My prices are not listed online.',
    },
    servicePrice: {
      id: 'manage-services.services.form.service-price',
      defaultMessage: 'Service price',
    },
    servicePriceDescriptionUSA: {
      id: 'manage-services.services.form.service-price-description-usa',
      defaultMessage: 'The price of your service, not including tax. We’ll use this to calculate your payout.',
    },
    servicePriceDescription: {
      id: 'manage-services.services.form.service-price-description',
      defaultMessage: 'The price of your service, including tax. We’ll use this to calculate your payout.',
    },
    linkToPrice: {
      id: 'manage-services.services.form.link-to-price',
      defaultMessage: 'Link to pricing',
    },
    linkToPriceTooltip: {
      id: 'manage-services.services.form.link-to-price-tooltip',
      defaultMessage: 'Where prices are provided on your customer-facing website.',
    },
    linkToPricePlaceholder: {
      id: 'manage-services.services.form.link-to-price-placeholder',
      defaultMessage: 'www.example.com/pricing',
    },
    linkToPriceDescription: {
      id: 'manage-services.services.form.link-to-price-description',
      defaultMessage: 'Required for price verification.',
    },
    invalidUrl: {
      id: 'manage-services.services.form.invalid-url',
      defaultMessage: 'A valid link is required.',
    },
    contactSupport: {
      id: 'manage-services.services.form.contact-support',
      defaultMessage: 'Contact support',
    },
    pricingQuestion: {
      id: 'manage-services.services.form.pricing-question',
      defaultMessage:
        'Hello, I have a question about the pricing for the service "{serviceName}" [ID {serviceId}, Venue ID {venueId}]',
    },
  }),
  list: defineMessages({
    itemDuration: {
      id: 'manage-services.services.list.item-duration',
      defaultMessage: '{duration, number} min',
    },
    itemNameDuration: {
      id: 'manage-services.services.list.item-name-duration',
      defaultMessage: '{serviceName} <grey>| {duration, number} min</grey>',
    },
    practitionersCount: {
      id: 'manage-services.services.list.practitioners-count',
      defaultMessage: '{practitionersCount, plural, one {# practitioner} other {# practitioners}}',
    },
    equipmentCount: {
      id: 'manage-services.services.list.equipment-count',
      defaultMessage: '{equipmentCount, number} equipment',
    },
    disabledTooltip: {
      id: 'manage-services.services.list.disabled-tooltip',
      defaultMessage: 'Services must be associated with practitioners or equipment to be bookable by members.',
    },
    pendingReviewTooltip: {
      id: 'manage-services.services.list.pending-review-tooltip',
      defaultMessage: 'This service is pending review.',
    },
  }),
  validator: defineMessages({
    fieldRequired: {
      id: 'manage-services.services.validator.field-required',
      defaultMessage: 'This field is required.',
    },
  }),
  success: defineMessages({
    serviceCreated: {
      id: 'manage-services.services.success.service-created',
      defaultMessage:
        "<title>You're almost done!</title><block>To make sure you are properly paid for this service type,</block><block>contact us to confirm the service pricing.</block><link>Contact Us</link>",
    },
    serviceUpdated: {
      id: 'manage-services.services.success.service-updated',
      defaultMessage: 'Service was successfully updated',
    },
    serviceDeleted: {
      id: 'manage-services.services.success.service-deleted',
      defaultMessage: 'Service was successfully deleted',
    },
    changesSaved: {
      id: 'manage-services.services.success.changes-saved',
      defaultMessage: 'Your changes have been successfully saved',
    },
  }),
  error: defineMessages({
    problemWithRequest: {
      id: 'manage-services.services.error.problem-with-request',
      defaultMessage: 'There was a problem with your request.',
    },
  }),
  empty: defineMessages({
    header: {
      id: 'manage-services.services.empty.header',
      defaultMessage: 'No services added yet',
    },
    subheader: {
      id: 'manage-services.services.empty.subheader',
      defaultMessage: 'Start now by adding service name and duration',
    },
    button: {
      id: 'manage-services.services.empty.button',
      defaultMessage: 'Add service',
    },
    emptyIcon: {
      id: 'manage-services.services.empty.empty-icon',
      defaultMessage: 'Empty services icon',
    },
  }),
  noEquipmentOrPractitionerModal: defineMessages({
    title: {
      id: 'manage-services.services.no-equipment-or-practitioner-modal.title',
      defaultMessage: 'Missing Practitioner or Equipment',
    },
    description: {
      id: 'manage-services.services.no-equipment-or-practitioner-modal.description',
      defaultMessage:
        'This service is missing at least one practitioner or equipment, making it unavailable to book by members. Would you like to save?',
    },
    stayOnPage: {
      id: 'manage-services.services.no-equipment-or-practitioner-modal.stay-on-page',
      defaultMessage: 'Stay on page',
    },
    saveAndLeave: {
      id: 'manage-services.services.no-equipment-or-practitioner-modal.save-and-leave',
      defaultMessage: 'Save & Leave',
    },
  }),
};
